import React, { useState, useRef } from 'react';
import {
  Send,
  Plus,
  Mic,
  MicOff,
  Image,
  Paperclip,
  X,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';

/**
 * ChatInput - Premium composer for Developer Agent
 * Features: Text input, file upload, voice toggle, smart suggestions
 */
const ChatInput = ({
  onSend,
  onFileUpload,
  disabled = false,
  placeholder = "Describe an issue, improvement, or task...",
  voiceEnabled = false,
  onVoiceToggle,
  showSuggestions = false,
  suggestions = [],
  onSuggestionClick,
  isLoading = false,
  className = ""
}) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if ((!message.trim() && attachments.length === 0) || disabled || isLoading) return;
    
    onSend({
      text: message.trim(),
      attachments: attachments
    });
    setMessage('');
    setAttachments([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
    if (onFileUpload) {
      onFileUpload(files);
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.id === id);
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter(a => a.id !== id);
    });
  };

  const toggleVoice = () => {
    setIsRecording(!isRecording);
    if (onVoiceToggle) {
      onVoiceToggle(!isRecording);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 px-1">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="relative group bg-slate-800/50 border border-slate-700 rounded-lg p-2 flex items-center gap-2"
            >
              {attachment.preview ? (
                <img
                  src={attachment.preview}
                  alt={attachment.name}
                  className="w-12 h-12 object-cover rounded"
                />
              ) : (
                <div className="w-12 h-12 bg-slate-700 rounded flex items-center justify-center">
                  <Paperclip className="w-5 h-5 text-slate-400" />
                </div>
              )}
              <span className="text-xs text-slate-400 max-w-[100px] truncate">
                {attachment.name}
              </span>
              <button
                onClick={() => removeAttachment(attachment.id)}
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Main Input Container */}
      <div className="relative bg-[rgba(10,38,28,0.8)] border border-[rgba(142,218,182,0.2)] rounded-2xl shadow-lg shadow-emerald-500/5">
        <div className="flex items-end gap-2 p-3">
          {/* File Upload Button */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.txt,.md,.js,.jsx,.ts,.tsx,.css,.json"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="h-10 w-10 rounded-xl text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 flex-shrink-0"
            data-testid="chat-upload-btn"
          >
            <Plus className="w-5 h-5" />
          </Button>

          {/* Text Input */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            rows={1}
            className="flex-1 bg-transparent border-0 text-white placeholder:text-slate-500 resize-none focus:outline-none focus:ring-0 text-sm py-2.5 max-h-32 min-h-[40px]"
            style={{ height: 'auto' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
            }}
            data-testid="chat-input"
          />

          {/* Voice Button */}
          {voiceEnabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleVoice}
              disabled={disabled}
              className={`h-10 w-10 rounded-xl flex-shrink-0 ${
                isRecording 
                  ? 'text-red-400 bg-red-500/10 hover:bg-red-500/20' 
                  : 'text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10'
              }`}
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </Button>
          )}

          {/* Send Button */}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={disabled || isLoading || (!message.trim() && attachments.length === 0)}
            className="h-10 w-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0 disabled:opacity-50"
            data-testid="chat-send-btn"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Smart Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 justify-center">
          {suggestions.map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => onSuggestionClick?.(suggestion)}
              disabled={disabled || isLoading}
              className="px-4 py-2 rounded-xl bg-slate-800/50 border border-slate-700 text-sm text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300 hover:bg-emerald-500/5 transition-all disabled:opacity-50"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatInput;
